import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { environment } from '../../environment/environment';
import type { ApiResponse } from '../interfaces/application';
import type { LastDeposit } from '../interfaces/reports';
import { buildHttpParams } from '../shared/utils';

@Injectable({
  providedIn: 'root',
})
export class ReportsService {

  protected readonly _http: HttpClient = inject(HttpClient);
  protected readonly _path: string = 'reports';

  public brandUtmSource(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/utm-source`, { params });
  }

  public financialCohortClientsDepositorsDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/clients-depositors/daily`, { params });
  }

  public financialCohortClientsDepositorsDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/clients-depositors/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortClientsDepositorsMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/clients-depositors/monthly`, { params });
  }

  public financialCohortClientsDepositorsMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/clients-depositors/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortDepositsDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/deposits/daily`, { params });
  }

  public financialCohortDepositsDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/deposits/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortDepositsMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/deposits/monthly`, { params });
  }

  public financialCohortDepositsMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/deposits/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortClientsRetentionDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/clients-retention/daily`, { params });
  }

  public financialCohortClientsRetentionDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/clients-retention/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortClientsRetentionMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/clients-retention/monthly`, { params });
  }

  public financialCohortClientsRetentionMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/clients-retention/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortClientsBettorsDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/clients-bettors/daily`, { params });
  }

  public financialCohortClientsBettorsDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/clients-bettors/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortClientsBettorsMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/clients-bettors/monthly`, { params });
  }

  public financialCohortClientsBettorsMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/clients-bettors/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortBetsDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/bets/daily`, { params });
  }

  public financialCohortBetsDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/bets/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortBetsMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/bets/monthly`, { params });
  }

  public financialCohortBetsMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/bets/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortGgrDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/ggr/daily`, { params });
  }

  public financialCohortGgrDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/ggr/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortGgrMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/ggr/monthly`, { params });
  }

  public financialCohortGgrMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/ggr/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortGgrCasinoDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/ggr-casino/daily`, { params });
  }

  public financialCohortGgrCasinoDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/ggr-casino/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortGgrCasinoMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/ggr-casino/monthly`, { params });
  }

  public financialCohortGgrCasinoMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/ggr-casino/monthly/csv`, { params, responseType: 'blob' });
  }

  public financialCohortGgrSportsDaily(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/ggr-sports/daily`, { params });
  }

  public financialCohortGgrSportsDailyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/ggr-sports/daily/csv`, { params, responseType: 'blob' });
  }

  public financialCohortGgrSportsMonthly(filters?: any): Observable<ApiResponse<any>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<any>>(`${environment.api}/${this._path}/cohort/ggr-sports/monthly`, { params });
  }

  public financialCohortGgrSportsMonthlyCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/${this._path}/cohort/ggr-sports/monthly/csv`, { params, responseType: 'blob' });
  }

  public lastDeposits(filters?: any): Observable<ApiResponse<LastDeposit[]>> {
    const params = buildHttpParams(filters);
    return this._http.get<ApiResponse<LastDeposit[]>>(`${environment.api}/last-deposits/query`, { params });
  }

  public lastDepositsCsv(filters?: any): Observable<Blob> {
    const params = buildHttpParams(filters);
    return this._http.get(`${environment.api}/last-deposits/query`, { params: params.set('format', 'csv'), responseType: 'blob' });
  }
}
